import React, { useEffect, useState, useRef } from "react";
import { Chart, registerables } from "chart.js";
import "chartjs-plugin-annotation";
import "./BarGraph.css";
import { v4 as uuidv4 } from "uuid";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useAuthToken } from "../TokenContext";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";

const DetailPRcountmanager = ({ initialPodIds }) => {
  const [metrics, setMetrics] = useState(null);
  const [talentUsers, setTalentUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const chartId = useRef(uuidv4());
  const chartInstance = useRef(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const intl = useIntl();

  const { user } = useSelector((state) => state.auth);
  const token = useAuthToken();

  const fetchDataForStakeholder = async () => {
    try {
      if (!token) {
        console.log('Token is missing. API requests will not be made.');
        return;
      }
      const payload = {};
      if (initialPodIds && initialPodIds.length > 0) {
        payload.pod_id = initialPodIds;
      } else if (user.data[0].client_code) {
        payload.org_code = user.data[0].client_code;
      }
      const response = await fetch(
        process.env.REACT_APP_STAKEHOLDER_GITHUBPRMETRICSSTAKEHOLDER,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json();
      
      if (response.status === 500) {
        setErrorMessage(intl.formatMessage({ id: "noDataAvailable" }));
      } else if (data?.body) {
        const responseJSON = JSON.parse(data.body);
        setMetrics(responseJSON);
        setTalentUsers(responseJSON.talent_users);
      }
    } catch (error) {
      console.log("Error fetching metrics:", error);
    }
  };

  useEffect(() => {
    Chart.register(...registerables);
    if (user.data[0].type === "stakeholder") {
      fetchDataForStakeholder();
    } else {
      if (initialPodIds && initialPodIds.length > 0) {
        fetchMetricsPod(initialPodIds);
      } else {
        fetchMetrics();
      }
    }
  }, [token, user.data[0].type, initialPodIds]);

  useEffect(() => {
    if (metrics) {
      displayChart();
    }
  }, [metrics, selectedUser]);

  const fetchMetricsPod = async (selectedPodIds) => {
    try {
      if (!token) {
        console.log('Token is missing. API requests will not be made.');
        return;
      }
      const response = await fetch(
        process.env.REACT_APP_CLIENT_GITHUBPRMETRICSMANAGERPODID,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ pod_id: selectedPodIds || null }),
        }
      );

      const data = await response.json();
     
      if (response.status === 500) {
        setErrorMessage("No Data Available");
      } else if (data?.body) {
        const responseJSON = JSON.parse(data.body);
        setMetrics(responseJSON);
        setTalentUsers(responseJSON.talent_users); // Set talentUsers from response
      }
    } catch (error) {
      console.log("Error fetching metrics:", error);
    }
  };

  const fetchMetrics = async () => {
    try {
      if (!token) {
        console.log('Token is missing. API requests will not be made.');
        return;
      }
      const response = await fetch(
        process.env.REACT_APP_CLIENT_GITHUBPRMETIRCSMANAGER,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            manager_id: user.data[0].emp_id,
            org_code: user.data[0].client_code,
          }),
        }
      );

      const data = await response.json();
      
      if (response.status === 500) {
        setErrorMessage(intl.formatMessage({ id: "noDataAvailable" }));
      } else if (data && data.body) {
        const responseJSON = JSON.parse(data.body);
        setMetrics(responseJSON);
        setTalentUsers(responseJSON.talent_users); // Set talentUsers from response
      }
    } catch (error) {
      console.log("Error fetching metrics:", error);
    }
  };

  const displayChart = () => {
    if (chartId.current) {
      const ctx = document.getElementById(chartId.current);

      if (metrics) {
        if (selectedUser) {
          // Find the selected user in talentUsers
          const user = talentUsers.find((u) => u.github_user === selectedUser);
          if (user) {
            // User-specific data
            const chartData = {
              labels: ["Submitted PRs", "Approved PRs"],
              datasets: [
                {
                  data: [user.submitted_pr_count, user.approved_pr_count],
                  backgroundColor: ["lavender", "turquoise"],
                },
              ],
            };

            if (chartInstance.current) {
              chartInstance.current.destroy();
            }

            chartInstance.current = new Chart(ctx, {
              type: "doughnut",
              data: chartData,
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  annotation: {},
                  legend: {
                    display: false,
                  },
                },
                layout: {
                  padding: {
                    top: 10,
                    bottom: 10,
                    left: 10,
                    right: 10,
                  },
                },
                tooltips: {
                  enabled: true,
                  mode: "single",
                  callbacks: {
                    label: function (tooltipItem, chartData) {
                      const dataIndex = tooltipItem.dataIndex;
                      const datasetIndex = tooltipItem.datasetIndex;
                      const value =
                        chartData.datasets[datasetIndex].data[dataIndex];
                      const label = chartData.labels[dataIndex];
                      return `${label}: ${value}`;
                    },
                  },
                },
                onClick: () => {
                  openModal();
                },
              },
            });
          }
        } else {
          // Default data based on total_prs
          const totalPrs = metrics.total_prs;

          const chartData = {
            labels: ["Total Submitted PRs", "Total Approved PRs"],
            datasets: [
              {
                data: [
                  totalPrs.Total_Submitted_PRs,
                  totalPrs.Total_Approved_PRs,
                ],
                backgroundColor: ["lavender", "turquoise"],
              },
            ],
          };

          if (chartInstance.current) {
            chartInstance.current.destroy();
          }

          chartInstance.current = new Chart(ctx, {
            type: "doughnut",
            data: chartData,
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                annotation: {},
                legend: {
                  display: false,
                },
              },
              layout: {
                padding: {
                  top: 10,
                  bottom: 10,
                  left: 10,
                  right: 10,
                },
              },
              tooltips: {
                enabled: true,
                mode: "single",
                callbacks: {
                  label: function (tooltipItem, chartData) {
                    const dataIndex = tooltipItem.dataIndex;
                    const datasetIndex = tooltipItem.datasetIndex;
                    const value =
                      chartData.datasets[datasetIndex].data[dataIndex];
                    const label = chartData.labels[dataIndex];
                    return `${label}: ${value}`;
                  },
                },
              },
              onClick: () => {
                openModal();
              },
            },
          });
        }
      }
    }
  };

  const openModal = () => {
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };
  const hasData =
    metrics &&
    (metrics.total_prs.Total_Submitted_PRs !== 0 ||
      metrics.total_prs.Total_Approved_PRs !== 0);


      let chartContent;
if (metrics) {
  if (hasData) {
    chartContent = <canvas id={chartId.current} width="150" height="150"></canvas>;
  } else {
    chartContent = <p>{intl.formatMessage({ id: "noDataAvailable" })}</p>;
  }
} else {
  chartContent = <p>{intl.formatMessage({ id: "noDataAvailable" })}</p>;
}

  return (
    <div className="chart-container">
      {errorMessage ? (
        <p>{errorMessage}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <h2 className="chart-heading">
            {intl.formatMessage({ id: "prCount" })}
          </h2>
          <br />
          {initialPodIds ? (
            <div>
              <select
                style={{ width: "12rem" }}
                onChange={(e) => {
                  setSelectedUser(e.target.value);
                  displayChart();
                }}
              >
                <option value="">
                  {intl.formatMessage({ id: "allReporters" })}
                </option>
                {talentUsers?.map((user) => (
                    <option key={user.github_user} value={user.github_user}>
                      {user.github_user}
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <></>
          )}

          <div className="chart-wrapper">
             {chartContent}
          </div>
        </div>
      )}

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        contentLabel="PR Summary Modal"
        style={{
          content: {
            width: "50%",
            maxWidth: "800px",
            margin: "auto",
            marginTop: "5rem",
          },
        }}
      >
        <h6>{intl.formatMessage({ id: "prSummary" })}</h6>
        <TableContainer component={Paper}>
          <Table
            component={Paper}
            sx={{
              maxWidth: 900,
              fontFamily: "Poppins,Medium",
              borderRadius: "10px",
            }}
            aria-label="simple table"
          >
            <TableHead
              sx={{
                backgroundColor: "#00B4D8",
                "& td, & th": {
                  fontFamily: "Poppins,Medium",
                  fontSize: "15px",
                  color: "white",
                  fontWeight: "600",
                },
              }}
            >
              <TableCell align="right">
                {intl.formatMessage({ id: "githubUser" })}
              </TableCell>
              <TableCell align="right">
                {intl.formatMessage({ id: "submittedPRCount" })}
              </TableCell>
              <TableCell align="right">
                {intl.formatMessage({ id: "approvedPRCount" })}
              </TableCell>
              <TableCell align="right">
                {intl.formatMessage({ id: "totalPRCount" })}
              </TableCell>
            </TableHead>
            <TableBody>
              {talentUsers?.map((project, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:last-child td, &:last-child th": {
                        border: 0,
                        fontFamily: "Poppins,Medium",
                        fontSize: "15px",
                      },
                      "&:nth-child(even)": {
                        backgroundColor: "#90E0EF",
                      },
                      "&:nth-child(odd)": {
                        backgroundColor: "#caf0f8",
                      },
                      "& td, & th": {
                        fontFamily: "Poppins,Medium",
                        fontSize: "15px",
                        color: "black",
                      },
                    }}
                  >
                    <TableCell align="right">{project.github_user}</TableCell>
                    <TableCell align="right">
                      {project.submitted_pr_count}
                    </TableCell>
                    <TableCell align="right">
                      {project.approved_pr_count}
                    </TableCell>
                    <TableCell align="right">
                      {project.total_pr_count}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <button onClick={closeModal}>
          {intl.formatMessage({ id: "done" })}
        </button>
      </Modal>
    </div>
  );
};

DetailPRcountmanager.propTypes = {
  initialPodIds: PropTypes.array.isRequired,
};
export default DetailPRcountmanager;
